import React, { useEffect } from 'react';
import {changeFormBusyState} from '../../../layout/user/store/actions/action';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as formJS from './formFunctions';
import SarasMsg from '../../../library/notifications/SarasMsg'
import SarasConfirmPrompt from "../../../library/notifications/SarasConfirmPrompt";
import FormLauncher from './FormLauncher';
import useFormApi from './useFormApi'
import Portal from '@material-ui/core/Portal';
import * as uiJS from '../../../helper/uiFunctions';

const viewSettings = {
    TriggerTypeLink: "link", //trigger link based show
    TriggerTypeCustom: "custom", //trigger on demand show
    TriggerTypeNone: "", //trigger manual actions (on click of action buttons)
}

//the purpose of this higher order component is:
// TriggerTypeCustom: "custom" 
// to trigger an on demand show request and then show the response data with a dynamically renderred view or a group of views
// fire the first show request based on the action prop, triggerAction, triggerShow to get the view or group of views as a response from the server
// and then render the view or a group of view depending on the requirement
// TriggerTypeLink: "link" where it fires show request based on the url params
//following data is required by the higer order component
//for TriggerType: "link": pass viewMap, triggerType="link"
//for TriggerType: "custom": pass viewMap, triggerType="custom", action, formObject (with masterkeys), triggerShow
//for TriggerType: "" : pass viewMap
const withView = function(ViewComponent, actName, depAct, noInitialDataNeeded) {
    return function(props) {
        const { viewMap, viewData: orgData, viewSwitcher,
            triggerType, formObject : parentFormObject, action : triggerAction, triggerShow,
            ...restProps } = props;

        const dispatchBusyState = useDispatch();

        const [actionName, setActionName] = React.useState(actName);
        const [depActions, setDepActions] = React.useState(depAct);
        const [eventCommand, setEventCommand] = React.useState(null);

        //key of the form that is registered in the global scope
        const [viewId, setViewId] = React.useState("");

        //store workspace of the current view, this will help if there is a trigger based on workspace
        const [wsIdState, setWSIdState] = React.useState(null);

        //store the full view data in the state
        const [fullViewData, setFullViewData] = React.useState(orgData);

        //the header rows and view rows can be altered via form functions, hence they are in a separate state
        //store the header view rows in the state
        const [viewHeaderData, setViewHeaderData] = React.useState(null);
        //store the view rows in the state
        const [viewData, setViewData] = React.useState(null);

        const [viewNotifications, setViewNotifications] = React.useState(null);

        const [viewMessages, setViewMessages] = React.useState(null);

        //dynamic view data will have value when a child form or popup is opened from within this form
        const [dynamicViewData, setDynamicViewData] = React.useState(null);

        const [confirmPromptData, setConfirmPromptData] = React.useState({})

        const history = useHistory();
        const formApi = useFormApi();

        //if composition then triggerType is link
        //if collection of views then triggerType is custom
        //if not provided then default triggerType is none

        //trigger ws data if following link changes
        //and trigger type is link
        const getWsDataFromLink = (forceTrigger) => {
            if(triggerType !== viewSettings.TriggerTypeLink) return;

            let dtIdPart = props.match.params.documentTypeId;
            let dIdPart = props.match.params.documentId;
            let wsIdPart = props.match.params.wsId;
    
            // console.log("withView outside", viewId, "ws in param", wsIdPart, "ws in state", wsIdState, "ws in master key", fullViewData && fullViewData.masterKeys.wsId);

            if(!wsIdState || wsIdState !== wsIdPart || forceTrigger){
                // console.log("withView inside", viewId, "ws in param", wsIdPart, "ws in state", wsIdState, "ws in master key", fullViewData && fullViewData.masterKeys.wsId);

                let customActionId = "show";
            
                //if ws id is provided then do not change the action id
                //else every time the previous object is replaced by the new object
                if(uiJS.compareNumber(dIdPart, 0) && !wsIdPart){
                    customActionId = "new";
                }

                let action = { actionId: customActionId, skipOrgData: 1,
                    actionParam: {"dtId": dtIdPart, "dId": dIdPart, "wsId": wsIdPart, "showModeID": "full"},
                    containerKey:""};

                onViewAction(action, "");
            }
        }

        useEffect(getWsDataFromLink, [props.match && props.match.params]);

        //trigger custom view data if following prop changes trigger action, parent object and trigger show 
        //and trigger type is custom
        let getCustomViewData = function() {
            if(triggerType !== viewSettings.TriggerTypeCustom) return;

            // console.log("withView Group outside", parentFormObject, triggerAction, triggerShow);

            if((!fullViewData || triggerAction) && triggerShow ){
                // console.log("withView Group inside", parentFormObject.masterKeys);

                let customAction = { actionId: triggerAction.actionId || "show", 
                    actionMessage: triggerAction.actionMessage || "",
                    actionParam: {...triggerAction.actionParam},
                    onSuccess: triggerAction.onSuccess,
                    onError: triggerAction.onError
                };

                if(customAction.actionMessage){
                    setViewNotifications({msgType:2, msg:customAction.actionMessage, messageList:{}});
                }
    
                onShowAction(customAction);
            }
        }
        
        useEffect(getCustomViewData, [parentFormObject, triggerAction, triggerShow]);

        let initialiseFormActions = function(){
            setActionName(actName);
            setDepActions(depAct);
        }

        useEffect(initialiseFormActions, [actName, depAct]);

        let initialiseFormState = function() {

            if(orgData && orgData.masterKeys){
                setWSIdState(orgData.masterKeys.wsId);
            }
            else{
                setWSIdState(null);
            }

            setFullViewData(orgData);
            
            //this function will return back form key which will be used for identifying the form
            //and ensure the same form doesn't get registered twice
            let newFormKey = formJS.regsiterForm(viewId);
            setViewId(newFormKey);
            
            // console.log("comp header issue view register", viewId, newFormKey);

            document.addEventListener('runFormCommand', triggerFormCommand, true);
            
            if (props.viewData && props.viewData.headerRows) {
                setViewHeaderData(props.viewData.headerRows);
            }

            if (props.viewData && props.viewData.viewRows) {
                setViewData(props.viewData.viewRows);
            }

            if (props.viewData && props.viewData.MessageListKeys 
                && props.viewData.MessageListKeys.length > 0)
            {
                setViewMessages(props.viewData.messageList);
            }
            else{
                setViewMessages(null);
            }

            // component unmount code
            return function () {
                // console.log("withView form uninitialised", actionName, viewId);

                document.removeEventListener('runFormCommand', triggerFormCommand, true);

                if (viewId) {
                    formJS.unRegsiterForm(viewId);
                }
            }
        }

        useEffect(initialiseFormState, [actionName, depActions, viewId, orgData]);

        let triggerFormCommand = (e) => {
            setEventCommand(e.detail);
        }

        let runFormCommand = () => {
            let command = eventCommand;

            if (command == null) return;

            if(command.commandName === "reload" && viewId){
                //if refresh self is marked and calling form key match with current form key
                let formSelf = command.callingAction.refreshMode === "self" && viewId === command.callingFormKey;

                //fire if all, or refresh self or actionname matches but it is not of the calling form (unless calling form is marked for self refresh)
                //TODO provide better support for parent, at the moment this code only supports composition as parent
                if(formSelf || 
                        (
                            (command.depActions === "all" || command.depActionsList.includes(actionName)) 
                            && viewId !== command.callingFormKey
                        )
                        || (
                            (command.depActions === "parent" || command.callingAction.refreshMode === "parent")
                            && triggerType === viewSettings.TriggerTypeLink
                        )
                    )
                {
                    let customParam = {}

                    //this is an indicator to the server that fresh data is required from the database before returning the response view
                    let reloadData = getValueFromKey(command.callingAction, "actionParam.reloadData", "0");

                    //add the default show mode id if the whole composition is being reloaded when trigger type is link
                    if(triggerType === viewSettings.TriggerTypeLink){
                        customParam = {...customParam, "showModeID": "full"}
                    }

                    //add the default params if it is a custom rendering
                    if(triggerType === viewSettings.TriggerTypeCustom && triggerAction && triggerAction.actionParam){
                        customParam = {...customParam, ...triggerAction.actionParam}
                    }

                    //also pass the path key and view key in action param if the self is refreshing
                    if(formSelf){
                        if(command.callingAction 
                            && command.callingAction.actionParam
                            && !uiJS.compareNumber(command.callingAction.excludePathKeys, 1))
                        {
                            customParam = {...customParam,
                                viewKey: getValueFromKey(command.callingAction, "actionParam.viewKey", ""),
                                pathKey: getValueFromKey(command.callingAction, "actionParam.pathKey", "")
                            }
                        }
                    }

                    let showAction = { actionId: "show", actionParam: {...customParam}, containerKey:"", viewDataReload: reloadData };

                    // console.log("withView run command", viewId, showAction, "ws in master key", fullViewData && fullViewData.masterKeys.wsId);
                    
                    onViewAction(showAction, "", "");
                }
            }

            //reset the value after the command is executed
            setEventCommand(null);
        }

        useEffect(runFormCommand, [eventCommand]);

        const onFormStateChange = (isBusy) => {
            dispatchBusyState(changeFormBusyState(formObject.formStateKey, isBusy));
        }
      
        let getFullViewDataOrMasterKeys = function (skipOrgData){
            let orgData = null;

            // console.log("withView Group get masterkeys", skipOrgData, fullViewData, parentFormObject && parentFormObject.masterKeys);

            if(skipOrgData && skipOrgData === 1) return orgData;

            if(fullViewData){
                orgData = fullViewData;
            }
            else if(parentFormObject){
                orgData = {
                    masterKeys:{...parentFormObject.masterKeys}
                };
            }

            return orgData;
        }

        let getDependantValue = function (formula, rowKey, headerKey, paramName) {
            let row = getFormRow(rowKey, headerKey);

            return formJS.getFormDependantValue(formula, row, paramName);
        }

        let getFormRow = function(rowKey, headerKey) {
            let row = null;

            //if header key is provided then return the row from header, else return the row from data
            if(headerKey){
                row = formJS.getFormRow(viewHeaderData, headerKey);
            }
            else{
                row = formJS.getFormRow(viewData, rowKey);
            }

            return row;
        }

        let getValueFromKey = function (data, key, defaultValue) {
            return formJS.getValueFromKey(data || fullViewData, key, defaultValue);
        }

        let getFormData = function(rowKey, headerKey, paramName) {
            if(paramName)
            {
                let row = getFormRow(rowKey, headerKey);

                let val = formJS.getFormRowVal(row, paramName);

                return val;
            }
            
            return null;
        }

        //ensure this code is called from 
        //onBlur of input or only when the value changes and not called for each key stroke
        let onFormValueChange = function(action, rowKey, headerKey, value) {
            updateViewDataValue(action.paramName, rowKey, headerKey, value);
        }

        let updateViewDataValue = function(paramName, rowKey, headerKey, value) {
            if (paramName) {
                
                let newFormValues;

                if(headerKey)
                {
                    newFormValues = formJS.updateFormValue(viewHeaderData, headerKey, paramName, value);
                    setViewHeaderData(newFormValues);
                }
                else
                {
                    newFormValues = formJS.updateFormValue(viewData, rowKey, paramName, value);
                    setViewData(newFormValues);
                }
            }
        }

        //this is the entry point for all actions
        let onViewAction = function (action, rowKey, headerKey) {
            if(viewNotifications) setViewNotifications(null);

            if(action.actionMessage){
                setViewNotifications({msgType:2, msg:action.actionMessage, messageList:{}});
            }

            if (action.actionId && formActions[action.actionId]) {
                formActions[action.actionId](action, rowKey, headerKey);
            }
        }

        let onActionSuccess = function(action, containerKey, rowKey, headerKey, response) {
            // console.log("debug message list on action success", action, containerKey, response);

            if(response.msg || Object.values(response.messageList).length > 0){
                //display the error message
                // console.log("budgetmsg", response.msg);
                setViewNotifications({msgType:response.isError, msg:response.msg, messageList:response.messageList || {}});
            }

            //analyse if this needs to be commented or not
            else{
                setViewNotifications(null);
            }

            if (response.mainViewData && response.mainViewData.MessageListKeys 
                && response.mainViewData.MessageListKeys.length > 0)
            {
                setViewMessages(response.mainViewData.messageList);
            }
            else{
                setViewMessages(null);
            }

            let dynamicViewObj = undefined;

            //update the state of the current view
            //this will be mostly used when show action is fired for reloading the view after dependent views are saved
            if(triggerType === viewSettings.TriggerTypeLink && response.mainViewData){
                // console.log("withView show view", viewId, "ws in response master key", response.mainViewData.masterKeys.wsId, "ws in state master key", fullViewData && fullViewData.masterKeys.wsId);

                let newUrl = uiJS.replacePathPart(props.match, 
                    "documentId", response.mainViewData.masterKeys.dId,
                    "wsId", response.mainViewData.masterKeys.wsId);

                setWSIdState(response.mainViewData.masterKeys.wsId);
                setFullViewData(response.mainViewData);

                history.replace(newUrl);
                // return;
            }

            if(triggerType === viewSettings.TriggerTypeCustom && response.mainViewData){
                //console.log("withView show view", viewId, "ws in response master key", response.mainViewData.masterKeys.wsId, "ws in state master key", fullViewData && fullViewData.masterKeys.wsId);

                setWSIdState(response.mainViewData.masterKeys.wsId);
                setFullViewData(response.mainViewData);
                // return;
            }

            //code for containerKey = redirect
            if(response && response.dataList){
                if(response.dataList.redirectAction === "show"){
                    let newDtId = response.dataList.dtId;
                    let newDId = response.dataList.dId;
                    let newWrksId = response.dataList.wsId;
                    let newDocView = response.dataList.docView;
                    let newDocModule = response.dataList.docModule;
                    
                    //console.log("withView show link", viewId, "ws in param", newWrksId, "ws in state master keys", fullViewData && fullViewData.masterKeys.wsId);

                    // setWSIdState(newWrksId);

                    //TODO url will not trigger render since wsid is not changing and withcomposition code will ignore the render
                    //build the url and navigate to that url
                    let newUrl = uiJS.getShowDocumentLink(newDtId, newDId, newWrksId, 0, newDocModule, newDocView);
                    history.replace(newUrl);
                    
                    //end further execution if form doesn't need to be refreshed
                    if((!action.refreshMode || action.refreshMode === "none") && !action.continueSession){
                        return;
                    }
                }
                else if(response.dataList.redirectAction === "search")
                {
                    let newDtId = response.dataList.dtId;
                    let newDocView = response.dataList.docView;
                    let newDocModule = response.dataList.docModule;

                    // setWSIdState(null);

                    let newUrl = uiJS.getSearchDocumentLink(newDtId, newDocModule, newDocView);
                    history.replace(newUrl);
                    return;
                }
            }

            //Scenarios decide to refresh the current form view state
            //or pass it on to the caller (to show a quick view popup) 
            //or show it in a custom way (handled by calling function)
            //or show it in a dynamic popup
            //or show it in a row (for row level editors)
            //or partial refresh of the data of existing row values
            if(containerKey === "custom"){
                if(response && response.mainViewData){
                    //only set the ojbect and pass it to the original caller since view will be displayed by the caller
                    dynamicViewObj = {viewKey: response.mainViewData.viewKey, viewData: response.mainViewData};
                }
            }
            else if(containerKey === "popup"){
                if(response && response.mainViewData){
                    //read the dynamic view information from response data to load that view in next render
                    dynamicViewObj = {viewKey: response.mainViewData.viewKey, viewData: response.mainViewData, action:action};
                    setDynamicViewData(dynamicViewObj);
                    // console.log("popup", dynamicViewObj);
                }
            }
            else if(containerKey === "row"){
                if(response){
                    if(action.actionId === "del" || response.actionId === "del"){
                        let newFormRows;

                        if(headerKey)
                        {
                            //del the new row with that in the response
                            newFormRows = formJS.deleteFormRow(viewHeaderData, headerKey);
                            if(response.mainViewData && response.mainViewData.headerRows){
                                newFormRows = formJS.replaceFormRows(newFormRows, response.mainViewData.headerRows);
                            }
                            setViewHeaderData(newFormRows);
                        }
                        else
                        {
                            //del the new row with that in the response
                            newFormRows = formJS.deleteFormRow(viewData, rowKey);
                            if(response.mainViewData && response.mainViewData.viewRows){
                                newFormRows = formJS.replaceFormRows(newFormRows, response.mainViewData.viewRows);
                            }
                            setViewData(newFormRows);
                        }
                    }
                    else if(action.actionId === "save" || 
                        action.actionId === "cancel" ||
                        action.actionId === "edit" ||
                        action.actionId === "new" ){

                        //TODO this logic will break if additional rows are returned, ensure api doesnot return any row if rows has to be deleted
                        let newFormRows;

                        if(headerKey)
                        {
                            //replace the entire row with the ones in response - matching the row key
                            if(response.mainViewData && response.mainViewData.headerRows){
                                newFormRows = formJS.replaceFormRows(viewHeaderData, response.mainViewData.headerRows);
                                setViewHeaderData(newFormRows);
                            }
                        }
                        else
                        {
                            //replace the entire row with the ones in response - matching the row key
                            if(response.mainViewData && response.mainViewData.viewRows){
                                newFormRows = formJS.replaceFormRows(viewData, response.mainViewData.viewRows);
                                setViewData(newFormRows);
                            }
                        }
                    }

                    if(response.mainViewData && fullViewData){
                        fullViewData.paging = response.mainViewData.paging;
                    }
                }
            }
            else{
                //update the state of the current view
                //this will be mostly used when show action is fired for reloading the view after dependent views are saved
                let newFormData = viewData;
                let newFormHeaderData = viewHeaderData;

                if(response && response.mainViewData){
                    
                    //if(Object.keys(response.mainViewData.viewRows).length > 0){
                    newFormData = response.mainViewData.viewRows;
                    //}

                    if (fullViewData) fullViewData.paging = response.mainViewData.paging;

                    if(Object.keys(response.mainViewData.childViews).length > 0){
                        if (fullViewData) fullViewData.childViews = response.mainViewData.childViews;
                    }

                    if(Object.keys(response.mainViewData.headerRows).length > 0){
                        if (fullViewData) fullViewData.headerRows = response.mainViewData.headerRows;
                        newFormHeaderData = response.mainViewData.headerRows;
                    }
                }

                if(headerKey)
                {
                    //scenario 1: handle blank row type, blank means the current key row type will get preference
                    if(response && response.partialRows) {
                        //replace the current values with the new partial values returned from the server
                        newFormHeaderData = formJS.replaceFormRowValues(newFormHeaderData, response.partialRows, "");
                    }
                }
                else
                {
                    //scenario 1: handle blank row type, blank means the current key row type will get preference, this is suitable when server is not specifying row type during partial update
                    if(response && response.partialRows) {
                        //replace the current values with the new partial values returned from the server
                        newFormData = formJS.replaceFormRowValues(newFormData, response.partialRows, "");
                    }
                }

                //scenario 2: handle row type separately: eg: rowtype v should get updated only in view rows and rowtype h should get updated only in header rows
                if(response && response.partialRows) {
                    //update header rows
                    newFormHeaderData = formJS.replaceFormRowValues(newFormHeaderData, response.partialRows, "h");
                    //update view rows
                    newFormData = formJS.replaceFormRowValues(newFormData, response.partialRows, "v");
                }

                //HACK update state only if either previous value was not null or new value is not null
                //it means the state has changed
                // console.log("comp header issue", viewId, viewHeaderData, newFormHeaderData, viewData, newFormData);
                // if(viewHeaderData != null || newFormHeaderData != null) {
                    setViewHeaderData(newFormHeaderData);
                // }

                // if(viewData != null || newFormData != null) {
                    setViewData(newFormData);
                // }
            }

            //call back the form custom action
            if(action && action.onSuccess) action.onSuccess(action, containerKey, rowKey, headerKey, dynamicViewObj || response);

            refreshDependentForms(action, containerKey, rowKey, headerKey, response);
        }
        
        let onResponseWithSuccess = function(action, containerKey, rowKey, headerKey, response) {
            onFormStateChange(0);

            onActionSuccess(action, containerKey, rowKey, headerKey, response);
        }

        let onResponseWithError = function(action, containerKey, rowKey, headerKey, response) {
            onFormStateChange(0);

            // {isError: 1, msg: '', messageList: {}}
            if(response.msg || response.messageList){
                 //display the error message
                setViewNotifications({msgType: response.isError, msg:response.msg, messageList:response.messageList || {}});
            }

            setViewMessages(null);

            //call back the form custom action
            if(action && action.onError) action.onError(action, containerKey, rowKey, headerKey, response);
        }

        let refreshDependentForms = function (action, containerKey, rowKey, headerKey, response) {

            //this is an indicator to the server that fresh data is required from the database before returning the response view
            let reloadData = getValueFromKey(response, "dataList.reloadData", "0");

            if(action.actionParam){
                action.actionParam["reloadData"] = reloadData;
            }
            else{
                action["actionParam"]={"reloadData":reloadData};
            }

            if(action.actionId === "save" 
                || action.actionId === "del" 
                || (action.refreshMode && action.refreshMode !== "none")
                ){
                formJS.refreshDependentForms(action, viewId, depActions);
            }
        }

        let onSearchAction = function(action, rowKey, headerKey) {
            if(action && action.isCompHeader === 1){
                //build search url
                //:module?/:view?/:actionName?/:documentTypeId?/:documentId?/:wsId?

                //build the url and navigate to that url
                let newPath = props.match;
                delete newPath.params["documentId"];
                delete newPath.params["wsId"];
                
                setWSIdState(null);

                let newUrl = uiJS.replacePathPart(newPath, "actionName", "search", "documentTypeId", fullViewData.masterKeys.dtId);

                history.replace(newUrl);
            }
        }

        let onShowAction = function(action, rowKey, headerKey) {
            // console.log("debug message list trigger show", action, actionName);

            if(action.passRowKey && action.passRowKey === 1) {
                formApi.runGenericAction(action, actionName, viewData, viewHeaderData, 
                    getFullViewDataOrMasterKeys(action.skipOrgData),
                    rowKey, headerKey, 0, onResponseWithSuccess, onResponseWithError);
            }
            else{
                formApi.runGenericAction(action, actionName, viewData, viewHeaderData, 
                    getFullViewDataOrMasterKeys(action.skipOrgData),
                    null, null, 0, onResponseWithSuccess, onResponseWithError);
            }
        }

        let onNewAction = function(action, rowKey, headerKey) {
            onFormStateChange(1);

            formApi.runGenericAction(action, actionName, viewData, viewHeaderData, 
                getFullViewDataOrMasterKeys(action.skipOrgData),
                rowKey, headerKey, 0, onResponseWithSuccess, onResponseWithError);
        }

        let onEditAction = function(action, rowKey, headerKey) {
            onFormStateChange(1);

            formApi.runGenericAction(action, actionName, viewData, viewHeaderData, fullViewData, rowKey, headerKey, 0, onResponseWithSuccess, onResponseWithError);
        }

        let onDeletePrompt = (action, rowKey, headerKey) => {
            setConfirmPromptData({message:"Do you really want to delete?", callback:onDeleteAction, action, rowKey, headerKey});
            //formApi.runGenericAction(action, actionName, viewData, viewHeaderData, fullViewData, rowKey, headerKey, 0, onResponseWithSuccess, onResponseWithError);
        }

        let onDeleteAction = (data) => {
            onFormStateChange(1);

            formApi.runGenericAction(data.action, actionName, viewData, viewHeaderData, fullViewData, data.rowKey, data.headerKey, 0, onResponseWithSuccess, onResponseWithError);
        }

        let onSaveAction = function(action, rowKey, headerKey) {
            // console.log("withview save trigger", viewId, actionName, action, "ws in state", wsIdState, "ws in state master keys", fullViewData && fullViewData.masterKeys.wsId);
            onFormStateChange(1);

            formApi.runGenericAction(action, actionName, viewData, viewHeaderData, fullViewData, rowKey, headerKey, 1, onResponseWithSuccess, onResponseWithError);
        }

        let onRefreshAction = function(action, rowKey, headerKey) {
            onFormStateChange(1);

            formApi.runGenericAction(action, actionName, viewData, viewHeaderData, fullViewData, rowKey, headerKey, 1, onResponseWithSuccess, onResponseWithError);
        }

        let onCancelAction = function(action, rowKey, headerKey) {
            onFormStateChange(1);

            formApi.runGenericAction(action, actionName, viewData, viewHeaderData, fullViewData, rowKey, headerKey, 0, onResponseWithSuccess, onResponseWithError);
        }

        let onUploadAction = function(action, rowKey, headerKey) {
            onFormStateChange(1);

            formApi.runUploadAction(action, actionName, viewData, viewHeaderData, fullViewData, rowKey, headerKey, onUploadSuccess, onResponseWithError);
        }

        let onImportAction = function(action) {
            formApi.runGenericAction(action, actionName, viewData, viewHeaderData, fullViewData, null, null, 0, onResponseWithSuccess, onResponseWithError);
        }

        let onPrintAction = function(action) {
            formApi.runGenericAction(action, actionName, viewData, viewHeaderData, fullViewData, null, null, 0, onPrintSuccess, onResponseWithError);
        }

        let onUploadSuccess = function(action, containerKey, rowKey, headerKey, response) {
            onFormStateChange(0);
                        
            setViewNotifications({msgType:0, msg:"File Uploaded Successfully", messageList:{}});
            if(headerKey)
            {
                updateViewDataValue("filename","", headerKey, response.dataList.filename || "");
                updateViewDataValue("ofilename", "",headerKey, response.dataList.ofilename || "");
                updateViewDataValue("ofileext", "",headerKey, response.dataList.ofileext || "");
                updateViewDataValue("newFileUploaded", "",headerKey, response.dataList.newFileUploaded || "0");
            }
            else
            {
                updateViewDataValue("filename", rowKey, "", response.dataList.filename || "");
                updateViewDataValue("ofilename", rowKey, "", response.dataList.ofilename || "");
                updateViewDataValue("ofileext", rowKey, "", response.dataList.ofileext || "");
                updateViewDataValue("newFileUploaded", rowKey, "", response.dataList.newFileUploaded || "0");
            }
        }

        let onPrintSuccess = function(action, containerKey, rowKey, headerKey, response) {
            let filename = response.dataList.fn;
            
            // console.log("report", filename, action);

            if(response.msg || Object.values(response.messageList).length > 0){
                //display the error message
                setViewNotifications({msgType:response.isError, msg:response.msg, messageList:response.messageList || {}});
            }

            //analyse if this needs to be commented or not
            else{
                setViewNotifications(null);
            }

            if(filename) {
                if(action && action.actionParam && uiJS.compareNumber(action.actionParam.rptInMemory, 1)){
                    if(uiJS.compareNumber(action.actionParam.directPrint, 1)){
                        // console.log("report display thermal print");
                        let dynamicViewObj = {viewKey: "vDirectPrint", viewData: filename, action:action};
                        setDynamicViewData(dynamicViewObj);
                    }
                }
                else{
                    uiJS.getFileDownloadLink(filename, "t", true);
                }
            }
        }
        
        let onDynamicViewClose = function(isRefresh, dynamicVData) {
            if(isRefresh === 1)
                formJS.refreshDependentForms({refreshMode:"parent"}, viewId, "parent");

            if(dynamicViewData && typeof dynamicViewData.action.onModalClose === "function"){
                dynamicViewData.action.onModalClose();
            }

            setDynamicViewData(null);
        }

        let getDynamicView = function(viewKey) {
            let DynamicComponent = viewMap[viewKey];
    
            //TODO if view not found in the current module view map than search for it in cmn controls view map
    
            if(!DynamicComponent)
            {
                return null;
            }
            else
            {
                return DynamicComponent;
            }
        }
    
        let convertStrToKeyValue = function(formParam) {
            return uiJS.convertStrToKeyValue(formParam);
        }

        let initialiseCustomActions = function(customAction, customDepActions) {
            if(customAction){
                setActionName(customAction);
            }
            if(customDepActions){
                setDepActions(customDepActions);
            }
        }

        let formObject = {
            onFormAction: onViewAction,
            onFormValueChange,
            getDependantValue,
            getFormRow,
            getFormData,
            getDynamicView,
            getValueFromKey,
            convertStrToKeyValue,
            initialiseCustomActions,
            refreshDependentForms,
            formStateKey: wsIdState,
            wsIdState,
            viewId,
            viewMap,
            viewSwitcher,
            actionName,
            depActions,
            masterKeys: fullViewData ? fullViewData.masterKeys : null,
            dataList: fullViewData ? fullViewData.dataList : null,
            docModule: fullViewData && fullViewData.masterKeys ? fullViewData.masterKeys.dataList.docModule : "",
            docView: fullViewData && fullViewData.masterKeys ? fullViewData.masterKeys.dataList.docView : "",
            dtId: fullViewData && fullViewData.masterKeys ? fullViewData.masterKeys.dtId : null,
            paging : fullViewData ? fullViewData.paging : null,
            childViews: fullViewData ? fullViewData.childViews : null
        }

        let formActions = {
            search: onSearchAction,
            show: onShowAction,
            refresh: onRefreshAction,
            new: onNewAction,
            edit: onEditAction,
            upload: onUploadAction,
            del: onDeletePrompt,
            save: onSaveAction,
            print: onPrintAction,
            cancel: onCancelAction,
            import: onImportAction
        };

        let renderModalForm = function() {
            if(dynamicViewData){
                // console.log("popup render", dynamicViewData, props.viewMap);
                //print debug information in the console log
                if(!dynamicViewData.viewKey){
                    return;
                }
                if(!dynamicViewData.viewData){
                    return;
                }

                return (
                    <Portal disablePortal={false}>
                        <FormLauncher onDynamicViewClose={onDynamicViewClose} viewSwitcher={viewSwitcher} 
                            viewMap={props.viewMap} viewData={dynamicViewData.viewData} 
                            viewMessages={dynamicViewData.viewData.messageList}
                            dynamicViewKey={dynamicViewData.viewKey}
                            dynamicViewData={dynamicViewData}></FormLauncher>
                    </Portal>
                );
            }

            //default render
            return null;
        }
        
        //scenario 1: for forms which are not dependent on data (eg: System/Update)
        //scenario 2: dependent on data and data available
        //passed fullview data as prop this may be used by view composition and view groups
        if(uiJS.compareNumber(noInitialDataNeeded, 1) || fullViewData){

            //scenario 1: for trigger type link, means composition 
            //scenario 2: for trigger type link, means collection of views
            //scenario 3: for trigger type link, means a single view
            if(triggerType === viewSettings.TriggerTypeLink){
                return (
                    <>
                        <div className="uiWrapper phViewArea">
                            <SarasMsg notification={viewNotifications} />
                            <div className="uiViewHeader"><FormLauncher formObject={formObject} 
                                viewMap={viewMap} compositionKey="compHeaderView" viewData={fullViewData} {...restProps}></FormLauncher></div>
                            <div className="uiViewDetail uiContents">
                                <ViewComponent formObject={formObject} viewData={fullViewData}
                                    viewMessages={fullViewData.messageList}
                                    viewMap={viewMap} {...restProps}></ViewComponent>
                            </div>
                        </div>
                        <SarasConfirmPrompt promptData={confirmPromptData} />
                        {renderModalForm()}
                    </>
                )
            }
            else if(triggerType === viewSettings.TriggerTypeCustom){
                return (
                    <>
                        <div className="uiWrapper">
                            <SarasMsg notification={viewNotifications} />
                            <div className="uiViewDetail uiContents">
                                <ViewComponent formObject={formObject} viewData={fullViewData}
                                    viewMessages={fullViewData.messageList}
                                    viewMap={viewMap} {...restProps}></ViewComponent>
                            </div>
                        </div>
                        <SarasConfirmPrompt promptData={confirmPromptData} />
                        {renderModalForm()}
                    </>
                )
            }

            return (
                <>
                    <SarasMsg notification={viewNotifications} />
                    <ViewComponent formObject={formObject} viewData={viewData}
                        viewMessages={viewMessages}
                        {...restProps}></ViewComponent>
                    <SarasConfirmPrompt promptData={confirmPromptData} />
                    {renderModalForm()}
                </>
            )
        }
        //scenario 3: dependent on data but data is missing
        else {
            return (
                <SarasMsg notification={viewNotifications} />
            );
        }
    }
}

export default withView;